import { Controller } from '@hotwired/stimulus'
import { broadcastInputChange } from '../_broadcast_change_utils'

export default class extends Controller<HTMLDivElement> {
  static targets = ['label', 'valueRemover', 'zeroValueInput', 'selectAll', 'loadingOverlay']

  declare readonly hasLabelTargets: boolean
  declare readonly labelTargets: HTMLLabelElement[]
  declare readonly hasValueRemoverTarget: boolean
  declare readonly valueRemoverTarget: HTMLButtonElement
  declare readonly hasSelectAllTarget: boolean
  declare readonly selectAllTarget: HTMLButtonElement
  declare readonly zeroValueInputTarget: HTMLInputElement
  declare readonly hasZeroValueInputTarget: boolean
  declare readonly hasLoadingOverlayTarget: boolean
  declare readonly loadingOverlayTarget: HTMLDivElement

  static values = {
    isVertical: Boolean,
    sendOnChange: Boolean
  }

  declare readonly isVerticalValue: boolean
  declare readonly sendOnChangeValue: boolean

  connect(): void {
    this.#verifyAndChangeInputStates()
  }

  #changeZeroValueInputActiveState(): void {
    if (!this.hasZeroValueInputTarget) return
    const checkedInputs = this.element.querySelectorAll('input:checked')
    const hasValues = checkedInputs.length > 0

    if (hasValues) {
      this.zeroValueInputTarget.setAttribute('disabled', '')
    } else {
      this.zeroValueInputTarget.removeAttribute('disabled')
    }
  }

  #changeSelectAllLabelState(): void {
    if (this.labelTargets.length === 0 || !this.hasSelectAllTarget) return
    const isAllChecked = this.labelTargets.every(
      label => label.querySelector('input')?.checked === true
    )
    const newText = isAllChecked ? 'Deselect all' : 'Select all'
    this.selectAllTarget.innerText = newText
  }

  #verifyAndChangeInputStates(): void {
    this.#changeZeroValueInputActiveState()
    this.#changeSelectAllLabelState()
  }

  #submitFormWithLoadingState(): void {
    this.element.classList.add('pointer-events-none')
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove('hidden')
    }
    this.element.closest('form')?.requestSubmit()
  }

  selectAllClicked(e: MouseEvent): void {
    e.preventDefault()
    const isAllChecked = this.labelTargets.every(
      label => label.querySelector('input')?.checked === true
    )
    this.labelTargets.forEach(label => {
      const input = label.querySelector('input')
      if (input !== null) {
        input.checked = !isAllChecked
        broadcastInputChange(input)
      }
    })
    if (this.hasValueRemoverTarget) {
      this.valueRemoverTarget.classList.remove('hidden')
    }
    this.#verifyAndChangeInputStates()
  }

  labelClicked(e: MouseEvent): void {
    // spacebar/enter register as click, so we make sure that this is really a click
    if (e.clientX > 0) {
      ;(e.target as HTMLLabelElement)?.blur()
    }
    if (this.hasValueRemoverTarget) {
      this.valueRemoverTarget.classList.remove('hidden')
    }
    this.#verifyAndChangeInputStates()
    const tagName = (e.target as HTMLElement)?.tagName
    if (tagName === 'INPUT') {
      broadcastInputChange(e.target as HTMLInputElement)
    }

    if (this.sendOnChangeValue) {
      this.#submitFormWithLoadingState()
    }
  }

  valueRemoverClicked(e: MouseEvent): void {
    e.preventDefault()
    this.labelTargets.forEach(label => {
      const input = label.querySelector('input')
      if (input !== null) {
        input.checked = false
      }
    })
    this.valueRemoverTarget.classList.add('hidden')
    this.#verifyAndChangeInputStates()
    const input = this.labelTargets[0].querySelector('input')
    if (input !== null) broadcastInputChange(input)
  }

  keyPressed(e: KeyboardEvent): void {
    const prevItemKey = this.isVerticalValue ? 'ArrowUp' : 'ArrowLeft'
    const nextItemKey = this.isVerticalValue ? 'ArrowDown' : 'ArrowRight'

    // puts focus on prev or next label
    if (e.key === prevItemKey || e.key === nextItemKey) {
      const increment = e.key === prevItemKey ? -1 : 1
      const focusedLabelIndex = this.labelTargets.findIndex(
        label => label.contains(document.activeElement) || label === document.activeElement
      )
      if (focusedLabelIndex > -1) {
        this.labelTargets[focusedLabelIndex + increment]?.focus()
        e.preventDefault()
      }
    }

    // ENTER doing the same as space
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.target?.click()
    }
  }
}
